import React from "react";
import ReactModal from "react-modal";
import { CloseButton } from "react-bootstrap";

export interface TableModalColumn<T = any> {
  label: string;
  render: (row: T, index: number) => React.ReactNode;
}

export interface TableModalProps<T = any> {
  isOpen: boolean;
  onRequestClose: () => void;
  title: string;
  columns: TableModalColumn<T>[];
  data: T[];
  onRowClick?: (row: T, index: number) => void;
  getRowKey?: (row: T, index: number) => string | number;
  emptyMessage?: string;
  contentLabel?: string;
  maxWidth?: string;
  /** When true, shows a loading state instead of table or empty message. */
  isLoading?: boolean;
  /** Pagination: total number of items across all pages. */
  totalCount?: number;
  /** Current 1-based page. */
  currentPage?: number;
  /** Total number of pages. */
  totalPages?: number;
  /** Page size (items per page). */
  pageSize?: number;
  /** Called when user requests another page. */
  onPageChange?: (page: number) => void;
  /** Overlay z-index when modal must sit above fullscreen charts (default 1100). */
  overlayZIndex?: number;
}

const defaultModalStyle = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto" as const,
    bottom: "auto" as const,
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "720px",
    maxHeight: "80vh",
    overflow: "auto" as const,
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
    border: "1px solid #e2e8f0",
    padding: "24px",
  },
  overlay: { backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1100 },
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 10px",
  fontWeight: 700,
  color: "#64748b",
  fontSize: "11px",
  letterSpacing: "0.06em",
  whiteSpace: "nowrap",
};

const tdStyle: React.CSSProperties = {
  padding: "12px 10px",
  color: "#1e293b",
};

function TableModal<T = any>({
  isOpen,
  onRequestClose,
  title,
  columns,
  data,
  onRowClick,
  getRowKey,
  emptyMessage = "No data to display.",
  contentLabel = "Table modal",
  maxWidth = "720px",
  isLoading = false,
  totalCount,
  currentPage = 1,
  totalPages = 1,
  pageSize = 10,
  onPageChange,
  overlayZIndex,
}: TableModalProps<T>) {
  const contentStyle = {
    ...defaultModalStyle.content,
    maxWidth,
  };

  const hasPagination = totalCount != null && totalPages != null && !isLoading;
  const showPaginationBar = hasPagination && totalPages > 1 && onPageChange;
  const startItem = totalCount != null && pageSize ? (currentPage - 1) * pageSize + 1 : 0;
  const endItem =
    totalCount != null && pageSize
      ? Math.min(currentPage * pageSize, totalCount)
      : 0;

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        content: contentStyle,
        overlay: {
          ...defaultModalStyle.overlay,
          zIndex: overlayZIndex ?? defaultModalStyle.overlay.zIndex,
        },
      }}
      contentLabel={contentLabel}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 700,
              color: "#1e293b",
            }}
          >
            {title}
          </h3>
          <CloseButton onClick={onRequestClose} aria-label="Close modal" />
        </div>
        {isLoading ? (
          <div
            style={{
              color: "#64748b",
              fontSize: "14px",
              padding: "32px",
              textAlign: "center",
            }}
          >
            Loading…
          </div>
        ) : data?.length ? (
          <>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                    {columns.map((col) => (
                      <th key={col.label} style={thStyle}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, idx) => (
                    <tr
                      key={getRowKey ? getRowKey(row, idx) : idx}
                      style={{
                        borderBottom: "1px solid #e2e8f0",
                        cursor: onRowClick ? "pointer" : "default",
                      }}
                      onClick={() => onRowClick?.(row, idx)}
                    >
                      {columns.map((col) => (
                        <td key={col.label} style={tdStyle}>
                          {col.render(row, idx)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasPagination && totalCount != null && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                  paddingTop: "12px",
                  borderTop: "1px solid #e2e8f0",
                }}
              >
                <span
                  style={{
                    fontSize: "13px",
                    color: "#64748b",
                  }}
                >
                  {startItem > 0 && endItem > 0
                    ? `Showing ${startItem}–${endItem} of ${totalCount.toLocaleString()}`
                    : `${totalCount.toLocaleString()} result${totalCount !== 1 ? "s" : ""}`}
                </span>
                {showPaginationBar && (
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <button
                      type="button"
                      onClick={() => onPageChange?.(currentPage - 1)}
                      disabled={currentPage <= 1}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                        background: currentPage <= 1 ? "#f1f5f9" : "#fff",
                        color: currentPage <= 1 ? "#94a3b8" : "#475569",
                        fontWeight: 500,
                        fontSize: "13px",
                        cursor: currentPage <= 1 ? "not-allowed" : "pointer",
                      }}
                    >
                      Previous
                    </button>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#64748b",
                        fontWeight: 500,
                      }}
                    >
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => onPageChange?.(currentPage + 1)}
                      disabled={currentPage >= totalPages}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "1px solid #e2e8f0",
                        background: currentPage >= totalPages ? "#f1f5f9" : "#fff",
                        color: currentPage >= totalPages ? "#94a3b8" : "#475569",
                        fontWeight: 500,
                        fontSize: "13px",
                        cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                      }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              color: "#64748b",
              fontSize: "14px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            {emptyMessage}
          </div>
        )}
      </div>
    </ReactModal>
  );
}

export default TableModal;